import {useHistoricalKpiMtmValue, ExpertSystemEquipmentType} from '@domain/expert-system'
import {DataContentWrapper} from '@hconnect/common/containers'
import {customThemeConstants, formatTimeZoneDate, getTimezoneName} from '@hconnect/uikit'
import {HPAvatar} from '@hconnect/uikit/src/lib2/components/avatar'
import CloseIcon from '@mui/icons-material/Close'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import {Box, Chip, IconButton, List, ListItem, Stack, Typography} from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import moment from 'moment-timezone'
import {FC, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {NavigateFunction, useNavigate} from 'react-router'

import {FEATURE_NAMESPACE, VALIDATION_TRANSLATIONS_KEY} from '../consts'
import {handleError} from '../hooks'

type HistoricalKpiDialogProps = {
  onClose: () => void
  kpiTargetEquipmentType: ExpertSystemEquipmentType
  hierarchyId: string
  equipmentName: string
  month: string
  year: number
}

export const HistoricalKpiDialog: FC<HistoricalKpiDialogProps> = ({
  onClose,
  kpiTargetEquipmentType,
  equipmentName,
  month,
  year,
  hierarchyId
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const navigate: NavigateFunction = useNavigate()

  const {data, isLoading, isError, refetch, error} = useHistoricalKpiMtmValue(
    kpiTargetEquipmentType,
    hierarchyId,
    `${year}-${month}`
  )

  useEffect(() => {
    if (error) {
      handleError(error, navigate)
    }
  }, [error, navigate])

  const selectedDate = moment(`${year}-${month}`)

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" data-test-id="info-dialog" fullWidth>
      <Box display="flex" alignItems="center" justifyContent="space-between" pr={3} pt={3} gap={1}>
        <Stack pl={3}>
          <DialogTitle variant="h3" sx={{pl: 0, pb: 0, pt: 0}} data-test-id="info-dialog-title">
            {selectedDate.format('MMMM YYYY')}
          </DialogTitle>
          <Typography variant="caption">{equipmentName}</Typography>
        </Stack>
        <IconButton aria-label="close" onClick={onClose} data-test-id="info-dialog-close">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{overflow: 'auto'}}>
        <DataContentWrapper
          showLoader={isLoading}
          isError={isError}
          data={data}
          retryFunction={refetch}
          progressSx={{color: 'primary.main'}}
        >
          <List sx={{py: 0}}>
            {data?.map((item, index) => {
              const formattedDate = formatTimeZoneDate(
                item.modifiedAt,
                getTimezoneName(),
                'DD MMMM, HH:mm',
                language
              )

              return (
                <ListItem
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    p: 0,
                    pb: index < data.length - 1 ? 1.5 : 0
                  }}
                  data-test-id={`history-item-${index}`}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={1}
                    ml={item.modifiedBy ? 0 : 1}
                  >
                    {item.modifiedBy && <HPAvatar userName={item.modifiedBy.name} />}
                    <Typography
                      variant="caption"
                      color="text.primary"
                      data-test-id={`history-modified-by-${index}`}
                    >
                      {item.modifiedBy?.name ?? '-'}
                    </Typography>
                    <Typography variant="caption">•</Typography>
                    <Typography variant="caption">{formattedDate}</Typography>
                  </Box>
                  <Chip
                    icon={<LayersOutlinedIcon color="primary" sx={{fontSize: 16}} />}
                    label={`Version ${item.version}: ${
                      item.value ??
                      (item.kpiCalculationError
                        ? t(VALIDATION_TRANSLATIONS_KEY[item.kpiCalculationError])
                        : '-')
                    }`}
                    sx={{
                      color: 'primary.main',
                      ml: 3,
                      backgroundColor: customThemeConstants().palette.semanticInfoFaded10,
                      border: `1px solid ${customThemeConstants().palette.semanticInfoFaded20}`
                    }}
                    data-test-id={`version-chip-${index}`}
                  />
                </ListItem>
              )
            })}
          </List>
        </DataContentWrapper>
      </DialogContent>
    </Dialog>
  )
}
